export const LIVE_EMBED_TIME_ZONE = 'America/Sao_Paulo';
export const OPERATIONAL_DAY_START_MINUTES = 100;

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function parseIsoDate(value) {
  if (!ISO_DATE_RE.test(value || '')) return null;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year
    || date.getUTCMonth() !== month - 1
    || date.getUTCDate() !== day
  ) return null;
  return { year, month, day, date };
}

export function isIsoDate(value) {
  return !!parseIsoDate(value);
}

export function buildDateChangeEvent(selectedDate, currentDate) {
  if (!isIsoDate(selectedDate) || !isIsoDate(currentDate)) return null;
  return {
    type: 'dale365-date-change',
    version: 1,
    date: selectedDate,
    isCurrent: selectedDate === currentDate,
  };
}

export function shiftIsoDate(value, days) {
  const parsed = parseIsoDate(value);
  if (!parsed || !Number.isInteger(days)) return '';
  parsed.date.setUTCDate(parsed.date.getUTCDate() + days);
  return parsed.date.toISOString().slice(0, 10);
}

export function currentOperationalDate(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: LIVE_EMBED_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(now);
  const read = (type) => Number(parts.find((part) => part.type === type)?.value || 0);
  const calendarDate = `${String(read('year')).padStart(4, '0')}-${String(read('month')).padStart(2, '0')}-${String(read('day')).padStart(2, '0')}`;
  const minutes = (read('hour') * 60) + read('minute');
  return minutes < OPERATIONAL_DAY_START_MINUTES ? shiftIsoDate(calendarDate, -1) : calendarDate;
}

function zonedLocalTimeToUtc({ year, month, day, hour, minute }) {
  const target = Date.UTC(year, month - 1, day, hour, minute, 0, 0);
  let estimate = target;
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: LIVE_EMBED_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const parts = formatter.formatToParts(new Date(estimate));
    const read = (type) => Number(parts.find((part) => part.type === type)?.value || 0);
    const represented = Date.UTC(
      read('year'), read('month') - 1, read('day'), read('hour'), read('minute'), read('second'),
    );
    const adjustment = target - represented;
    if (!adjustment) break;
    estimate += adjustment;
  }
  return estimate;
}

export function millisecondsUntilNextOperationalBoundary(now = new Date()) {
  const nextDate = parseIsoDate(shiftIsoDate(currentOperationalDate(now), 1));
  if (!nextDate) return 60000;
  const boundary = zonedLocalTimeToUtc({
    year: nextDate.year,
    month: nextDate.month,
    day: nextDate.day,
    hour: Math.floor(OPERATIONAL_DAY_START_MINUTES / 60),
    minute: OPERATIONAL_DAY_START_MINUTES % 60,
  });
  return Math.max(1, boundary - now.getTime());
}

export function advanceOperationalContext({ selectedDate, currentDate, nextDate }) {
  if (!isIsoDate(selectedDate) || !isIsoDate(currentDate) || !isIsoDate(nextDate) || nextDate <= currentDate) {
    return {
      changed: false,
      currentDate,
      selectedDate,
      selectionAdvanced: false,
    };
  }
  const selectionAdvanced = selectedDate === currentDate;
  return {
    changed: true,
    currentDate: nextDate,
    selectedDate: selectionAdvanced ? nextDate : selectedDate,
    selectionAdvanced,
  };
}

export function reconcileOperationalContext({ context, currentDate, now = new Date() }) {
  if (!isIsoDate(context?.date)) return null;
  let nextCurrentDate = isIsoDate(currentDate) ? currentDate : currentOperationalDate(now);
  if (context.isCurrent === true) nextCurrentDate = context.date;
  else if (context.isCurrent === false) nextCurrentDate = currentOperationalDate(now);
  return { selectedDate: context.date, currentDate: nextCurrentDate };
}

export function formatShortDate(value) {
  const parsed = parseIsoDate(value);
  if (!parsed) return '--/--';
  return `${String(parsed.day).padStart(2, '0')}/${String(parsed.month).padStart(2, '0')}`;
}

export function formatLongDate(value) {
  const parsed = parseIsoDate(value);
  if (!parsed) return 'Data inválida';
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'UTC',
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(parsed.date);
}

export function monthLabel(value) {
  const parsed = parseIsoDate(value);
  if (!parsed) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'UTC',
    month: 'long',
    year: 'numeric',
  }).format(parsed.date);
}

export function startOfMonth(value) {
  const parsed = parseIsoDate(value);
  if (!parsed) return '';
  return `${String(parsed.year).padStart(4, '0')}-${String(parsed.month).padStart(2, '0')}-01`;
}

export function shiftMonth(value, months) {
  const parsed = parseIsoDate(startOfMonth(value));
  if (!parsed || !Number.isInteger(months)) return '';
  parsed.date.setUTCMonth(parsed.date.getUTCMonth() + months);
  return parsed.date.toISOString().slice(0, 10);
}

export function buildCalendarDays({ month, selectedDate, today, minDate, maxDate }) {
  const monthStart = startOfMonth(month);
  const parsed = parseIsoDate(monthStart);
  if (!parsed) return [];
  const mondayOffset = (parsed.date.getUTCDay() + 6) % 7;
  const gridStart = shiftIsoDate(monthStart, -mondayOffset);
  const monthPrefix = monthStart.slice(0, 7);

  return Array.from({ length: 42 }, (_, index) => {
    const date = shiftIsoDate(gridStart, index);
    return {
      date,
      day: Number(date.slice(-2)),
      inCurrentMonth: date.startsWith(monthPrefix),
      selected: date === selectedDate,
      today: date === today,
      disabled: !isIsoDate(date)
        || (!!minDate && date < minDate)
        || (!!maxDate && date > maxDate),
    };
  });
}

export function intersectDateBounds({
  rankingsFirstDate,
  messagesFirstDate,
  currentDate,
  allowMessageFallback = false,
}) {
  if (!isIsoDate(currentDate)) return { minDate: '', maxDate: '' };
  if (!isIsoDate(messagesFirstDate)) {
    return null;
  }
  if (!isIsoDate(rankingsFirstDate)) {
    if (!allowMessageFallback) return null;
    return { minDate: messagesFirstDate, maxDate: currentDate };
  }
  return {
    minDate: rankingsFirstDate > messagesFirstDate ? rankingsFirstDate : messagesFirstDate,
    maxDate: currentDate,
  };
}
